"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Check, QrCode } from "lucide-react";
import Link from "next/link";

const TARGET_GUESTS = 500;

export default function QrDoorHero() {
  const { t } = useLang();
  const [guests, setGuests] = useState(0);

  // Animated counter — guests through the door
  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const dur = 4000;
    const tick = (now: number) => {
      const tt = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - tt, 3);
      setGuests(Math.round(TARGET_GUESTS * eased));
      if (tt < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-qr-door.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.20) 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#F472B6" }}
        >
          {t("Door check-in · Scan to enter", "Check-in en puerta · Scan para entrar")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6"
        >
          {t("Every guest.", "Cada invitado.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Scanned in seconds.", "Escaneado en segundos.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Doorman points the phone, the QR reads, the ticket validates, guest in. The line moves. Used ticket? Big red X with the time it was first scanned.",
            "El doorman apunta el teléfono, el QR se lee, el ticket valida, invitado adentro. La fila avanza. ¿Ticket usado? X roja grande con la hora del primer escaneo."
          )}
        </motion.p>

        {/* Hero showcase: iPhone scanner + live counter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center max-w-[1100px] mx-auto mb-12"
        >
          {/* iPhone scanner mockup */}
          <div className="lg:col-span-7 flex justify-center lg:justify-end relative">
            <div
              className="absolute -inset-10 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 65%)" }}
            />
            <IPhoneFrame>
              <div className="h-full flex flex-col bg-[#0A0814] text-white">
                {/* Status bar */}
                <div className="h-7 flex items-center justify-between px-4 text-[8px] font-bold">
                  <span>11:32</span>
                  <span>5G</span>
                </div>

                {/* Header */}
                <div className="px-3 py-2.5 border-b border-white/5">
                  <div className="text-[8px] font-black uppercase tracking-widest text-white/45">
                    {t("Door · Main Entrance", "Puerta · Entrada Principal")}
                  </div>
                  <div className="text-[12px] font-black text-white">NEON RUSH · Friday</div>
                </div>

                {/* Camera view with QR */}
                <div className="flex-1 relative bg-[#0F0B1F] flex items-center justify-center overflow-hidden">
                  {/* Scanner viewfinder */}
                  <div className="relative w-[60%] aspect-square">
                    {/* Corner brackets */}
                    {[
                      { tl: "top-0 left-0 border-t-2 border-l-2 rounded-tl-md" },
                      { tl: "top-0 right-0 border-t-2 border-r-2 rounded-tr-md" },
                      { tl: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-md" },
                      { tl: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-md" },
                    ].map((c, i) => (
                      <div
                        key={i}
                        className={`absolute w-5 h-5 ${c.tl}`}
                        style={{ borderColor: "#F472B6" }}
                      />
                    ))}

                    {/* Scan line */}
                    <motion.div
                      animate={{
                        top: ["0%", "100%", "0%"],
                      }}
                      transition={{
                        duration: 2.0,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="absolute left-2 right-2 h-px"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, #F472B6, transparent)",
                        boxShadow: "0 0 8px rgba(244,114,182,0.7)",
                      }}
                    />

                    {/* QR code centered */}
                    <div className="absolute inset-4 flex items-center justify-center">
                      <QrCodeMockup />
                    </div>
                  </div>
                </div>

                {/* Success bar */}
                <motion.div
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
                  className="px-3 py-3 flex items-center gap-3"
                  style={{
                    background: "linear-gradient(135deg, rgba(46,204,113,0.18) 0%, rgba(46,204,113,0.08) 100%)",
                    borderTop: "1px solid rgba(46,204,113,0.4)",
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.4, duration: 0.3 }}
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "#2ECC71" }}
                  >
                    <Check className="w-4 h-4 text-white" strokeWidth={3.5} />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black text-white truncate">
                      MARIA GONZÁLEZ
                    </div>
                    <div className="text-[9px] font-bold" style={{ color: "#2ECC71" }}>
                      GA · {t("In", "Adentro")} · 11:32:04
                    </div>
                  </div>
                  <div className="text-[10px] font-black tabular-nums" style={{ color: "#2ECC71" }}>
                    {t("Valid", "Válido")}
                  </div>
                </motion.div>
              </div>
            </IPhoneFrame>
          </div>

          {/* Live guest counter */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-7 w-full max-w-[340px] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("Through the door", "Adentro")}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#F472B6" }}>
                    Live
                  </span>
                </div>
              </div>

              <div
                className="text-[80px] font-black tabular-nums leading-none mb-2"
                style={{
                  background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {guests}
              </div>
              <div className="text-[12px] font-bold text-white/55">
                {t("of 500 capacity", "de 500 capacidad")}
              </div>

              {/* Capacity bar */}
              <div className="mt-5 h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  animate={{ width: `${(guests / TARGET_GUESTS) * 100}%` }}
                  transition={{ duration: 0.1 }}
                  className="h-full rounded-full"
                  style={{ background: "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)" }}
                />
              </div>

              {/* Latency badge */}
              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[10px] font-bold">
                <span className="uppercase tracking-wider text-white/45">{t("Duplicates caught", "Duplicados detectados")}</span>
                <span className="tabular-nums" style={{ color: "#F472B6" }}>4</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] font-bold">
                <span className="uppercase tracking-wider text-white/45">{t("Door scanners", "Scanners en puerta")}</span>
                <span className="text-white tabular-nums">3</span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup?vertical=nightlife"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 60px rgba(124, 58, 237, 0.5)",
            }}
          >
            {t("Move the line →", "Mueve la fila →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[260px] h-[520px] bg-[#0a0a0a] rounded-[40px] p-[6px] shadow-[0_40px_100px_rgba(0,0,0,0.6)] relative">
      <div className="w-full h-full rounded-[34px] overflow-hidden bg-black relative">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}

// CSS-only QR code mockup — looks legit at small sizes
function QrCodeMockup() {
  // Pseudo-random pattern (fixed seed for determinism)
  const cells = Array.from({ length: 21 * 21 }).map((_, i) => {
    // Fixed corner squares
    const x = i % 21;
    const y = Math.floor(i / 21);
    // Position markers (3 corners)
    const inTL = x < 7 && y < 7;
    const inTR = x >= 14 && y < 7;
    const inBL = x < 7 && y >= 14;
    if (inTL || inTR || inBL) {
      const lx = inTL ? x : inTR ? x - 14 : x;
      const ly = inTL ? y : inTR ? y : y - 14;
      const onEdge = lx === 0 || lx === 6 || ly === 0 || ly === 6;
      const inCenter = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4;
      return onEdge || inCenter;
    }
    // Pseudo-noise
    return ((x * 7 + y * 13 + x * y * 3) % 5) < 2;
  });

  return (
    <div className="w-full h-full bg-white rounded-md p-[6%] grid grid-cols-[repeat(21,1fr)] gap-[2%]">
      {cells.map((on, i) => (
        <div
          key={i}
          className="aspect-square"
          style={{ backgroundColor: on ? "#0A0814" : "transparent" }}
        />
      ))}
    </div>
  );
}
